import React from 'react';
import { Package, Ship, Calendar, MapPin } from 'lucide-react';
import { getStatusLabel } from '../../../services/containerService';

export default function ContainerInfoCard({ container }) {
  if (!container) {
    return null;
  }

  const getStatusColor = (status) => {
    const colors = {
      'AU_PORT_LE_HAVRE_ANVERS': 'bg-gray-100 text-gray-800',
      'EN_TRANSIT_MARITIME': 'bg-blue-100 text-blue-800',
      'ARRIVE_A_ABIDJAN': 'bg-purple-100 text-purple-800',
      'DEDOUANEMENT_EN_COURS': 'bg-yellow-100 text-yellow-800',
      'TRANSFERT_A_ENTREPOT': 'bg-orange-100 text-orange-800',
      'DISTRIBUTION_EN_COURS': 'bg-indigo-100 text-indigo-800',
      'CONTENEUR_TERMINE': 'bg-green-100 text-green-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Informations Conteneur</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Numéro de conteneur</span>
            <span className="font-semibold text-gray-900">
              {container?.container_number || 'N/A'}
            </span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Statut</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(container?.status)}`}>
              {getStatusLabel(container?.status) || 'Statut inconnu'}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Code Conteneur</p>
                <p className="text-base font-bold text-blue-600">{container?.container_code}</p>
              </div>
            </div>
          </div>

          {container?.vessel_name && (
            <div className="flex items-start gap-3">
              <Ship className="w-5 h-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 font-medium">Navire</p>
                <p className="text-sm font-semibold text-gray-900">{container?.vessel_name}</p>
                {container?.shipping_line && (
                  <p className="text-xs text-gray-600 mt-0.5">{container?.shipping_line}</p>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {container?.departure_port && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium">Port de départ</p>
                  <p className="text-sm text-gray-900">{container?.departure_port}</p>
                </div>
              </div>
            )}

            {container?.destination_port && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium">Port de destination</p>
                  <p className="text-sm text-gray-900">{container?.destination_port}</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {container?.departure_date && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium">Date de départ</p>
                  <p className="text-sm text-gray-900">
                    {new Date(container?.departure_date)?.toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            )}

            {container?.eta_date && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium">Date d'arrivée estimée</p>
                  <p className="text-sm text-gray-900">
                    {new Date(container?.eta_date)?.toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>

          {container?.notes && (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 font-medium mb-2">Notes</p>
              <p className="text-sm text-gray-700">{container?.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}